'use client';

import { arrow, clockIcon, editEnd, editStart } from '@/assets';
import { AnswerTemplateVariableHint } from '@/components/answer-templates/AnswerTemplateVariableHint';
import { AnswerTemplatesControl } from '@/components/answer-templates/AnswerTemplatesControl';
import { QuotaActionButton } from '@/components/billing/QuotaTopUpButton';
import { ChatControllerBindings } from '@/components/chat/chat-controller.types';
import { DialogActionGrid } from '@/components/dialog-actions/DialogActionGrid';
import { ChatMessage } from '@/contracts/marketplace/chat-message';
import { createPublicClientConfig } from '@/config/public-client-config';
import { Marketplace } from '@/enums/marketplace';
import { UIPriority } from '@/enums/ui-priorities';
import { formatDateTimeInGmtPlus3 } from '@/shared/date-time-gmt-plus-3';
import { getSelectedAutoRepliesRunLabels } from '@/shared/auto-replies-labels';
import { formatReviewTableTextForDisplay } from '@/shared/format-review-table-text-for-display';
import Button from '@/ui/Button';
import DisabledReasonTooltip from '@/ui/DisabledReasonTooltip';
import Modal from '@/ui/Modal';
import Rating from '@/ui/Rating';
import Textarea from '@/ui/Textarea';
import clsx from 'clsx';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

type LabeledTextareaProps = {
    title: string;
    value: string;
    setValue: (value: string) => void;
    disabled: boolean;
    readOnly?: boolean;
    placeholder: string;
};

const LabeledTextarea = ({ title, value, setValue, disabled, readOnly, placeholder }: LabeledTextareaProps) => (
    <fieldset className="min-w-0 rounded-lg border border-gray-300 px-3 pb-2">
        <legend className="px-1 text-xs text-slate-600">{title}</legend>
        <Textarea
            value={value}
            setValue={setValue}
            disabled={disabled}
            readOnly={readOnly}
            placeholder={placeholder}
            className="min-h-[126px] border-0 p-1"
            autoGrow
        />
    </fieldset>
);

const stringifyChatMessageForDebug = (message: ChatMessage): string => {
    try {
        return JSON.stringify(message);
    } catch {
        return '{"error":"failed_to_stringify_chat_message"}';
    }
};

export const ChatView = ({
    review,
    history,
    answerTargetReviewFeedItemId,
    answerTargetKey,
    hasAnswerJob,
    isThreadEditingLocked,
    messageText,
    setMessageText,
    editedMessageText,
    setEditedMessageText,
    aiGenerateLabel,
    aiGenerateDisabledReason,
    submitDisabledReason,
    isAiGenerateQuotaExhausted,
    submitButtonLabel,
    isManualEditOpened,
    aiErrorText,
    isEditing,
    textAreaDisabled,
    aiGenerateDisabled,
    canShowManualEditButton,
    canSubmitMessage,
    changeEditing,
    onAnswerTargetSelect,
    isAnswerTargetSelectable,
    submitSendChatMessage,
    generateAiChatMessage,
    onOpenSelectedAutoReplies,
    openManualEdit,
    copyMessageToEdited,
    openedPhotos,
    openedPhoto,
    isPhotoLoading,
    onPhotoPreviewOpen,
    onPhotoPreviewClose,
    onPreviousPhoto,
    onNextPhoto,
    onOpenedPhotoResolved,
    answerTemplates,
    isTemplatesLoading,
    templatesErrorText,
    isTemplatesMenuOpen,
    isTemplatesEditorShown,
    editingTemplateId,
    templateNameDraft,
    templateTextDraft,
    templateFormErrorText,
    isTemplateSaving,
    isTemplateDeleting,
    canSaveTemplate,
    canDeleteTemplate,
    onToggleTemplatesMenu,
    onCloseTemplatesMenu,
    onOpenTemplatesEditor,
    onCloseTemplatesEditor,
    onApplyTemplate,
    onSelectTemplateForEdit,
    onStartNewTemplateDraft,
    onTemplateNameDraftChange,
    onTemplateTextDraftChange,
    onSaveTemplate,
    onDeleteTemplate,
}: ChatControllerBindings) => {
    const [hoveredMessageDebugJson, setHoveredMessageDebugJson] = useState<string | null>(null);
    const historyScrollContainerRef = useRef<HTMLDivElement | null>(null);
    const replyTargetMessageRef = useRef<HTMLDivElement | null>(null);
    const didAutoScrollReplyTargetRef = useRef(false);
    const isChatHistoryDebugJsonEnabled = createPublicClientConfig().isChatHistoryDebugJsonEnabled;
    const selectedAutoRepliesLabels = getSelectedAutoRepliesRunLabels('ru');

    const handleMessageMouseEnter = (message: ChatMessage) => {
        if (!isChatHistoryDebugJsonEnabled) {
            return;
        }

        setHoveredMessageDebugJson(stringifyChatMessageForDebug(message));
    };

    const handleMessageMouseLeave = () => {
        if (!isChatHistoryDebugJsonEnabled) {
            return;
        }

        setHoveredMessageDebugJson(null);
    };

    useEffect(() => {
        didAutoScrollReplyTargetRef.current = false;
    }, [review.id]);

    useEffect(() => {
        if (didAutoScrollReplyTargetRef.current || !answerTargetKey) {
            return;
        }

        const scrollContainer = historyScrollContainerRef.current;
        const replyTarget = replyTargetMessageRef.current;
        if (!scrollContainer || !replyTarget) {
            return;
        }

        if (typeof replyTarget.scrollIntoView !== 'function') {
            return;
        }

        replyTarget.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
            behavior: 'auto',
        });
        didAutoScrollReplyTargetRef.current = true;
    }, [answerTargetKey, history.length]);

    return (
        <div className="bg-white h-full w-full rounded-lg flex min-h-0 flex-col gap-2 p-2 pb-2 overflow-hidden">
            <div
                ref={historyScrollContainerRef}
                className="min-h-0 flex flex-1 flex-col gap-2 overflow-y-auto pr-1 pt-1"
            >
                {history.map((message, idx) =>
                    message.isAnswer
                        ? (() => {
                              const isOfficialMessage = message.isOfficial === true;
                              const answerCreatedDateValue =
                                  message.createdDate ||
                                  (!message.inQueue && message.reviewFeedItemId === review.id
                                      ? review.answer?.updatedDate
                                      : undefined);
                              const answerCreatedDateLabel = answerCreatedDateValue
                                  ? formatDateTimeInGmtPlus3(answerCreatedDateValue) || answerCreatedDateValue
                                  : undefined;
                              const canEditAnswerMessage =
                                  !hasAnswerJob &&
                                  !isThreadEditingLocked &&
                                  Boolean(message.editable) &&
                                  !message.inQueue &&
                                  message.reviewFeedItemId === answerTargetReviewFeedItemId &&
                                  review.platform !== Marketplace.Ozon;

                              return (
                                  <div
                                      key={idx}
                                      className={clsx(
                                          'self-end p-2.5 rounded-lg max-w-[80%] text-sm leading-5 whitespace-pre-line wrap-break-word',
                                          isOfficialMessage ? 'bg-emerald-100' : 'bg-blue-100',
                                      )}
                                      data-reply-target="false"
                                      onMouseEnter={() => handleMessageMouseEnter(message)}
                                      onMouseLeave={handleMessageMouseLeave}
                                  >
                                      {message.text}

                                      <div
                                          className={clsx(
                                              'text-gray-500 text-xs mt-1 flex justify-end',
                                              answerCreatedDateLabel && 'gap-4 justify-between',
                                          )}
                                      >
                                          {answerCreatedDateLabel && <span>{answerCreatedDateLabel}</span>}

                                          <span className="inline-flex">
                                              <div className="flex gap-[5px] items-center">
                                                  {isOfficialMessage && <p>Официальный ответ</p>}
                                                  {message.inQueue && <Image src={clockIcon} alt={''} width={16} />}
                                                  <p>{message.state}</p>
                                              </div>
                                              {canEditAnswerMessage && (
                                                  <Image
                                                      onClick={changeEditing}
                                                      src={isEditing ? editEnd : editStart}
                                                      alt={'edit'}
                                                      width={12}
                                                      height={12}
                                                      className="select-none cursor-pointer ml-2"
                                                  />
                                              )}
                                          </span>
                                      </div>
                                  </div>
                              );
                          })()
                        : (() => {
                              const preparedReviewText = formatReviewTableTextForDisplay({
                                  author: message.author,
                                  orderStatus: message.orderStatus,
                                  text: message.text,
                                  pros: null,
                                  cons: null,
                                  bables: null,
                              });
                              const reviewPhotos =
                                  message.photos ||
                                  (message.reviewFeedItemId === review.id ? (review.photos ?? []) : []);
                              const reviewCreatedDateValue =
                                  message.createdDate ||
                                  (message.reviewFeedItemId === review.id ? review.createdDate : undefined);
                              const reviewCreatedDateLabel = reviewCreatedDateValue
                                  ? formatDateTimeInGmtPlus3(reviewCreatedDateValue) || reviewCreatedDateValue
                                  : undefined;
                              const isCommentMessage = Boolean(message.reviewCommentId);
                              const reviewRating =
                                  Number(message.rating ?? (isCommentMessage ? 0 : review.rating)) || 0;
                              const shouldShowRating = !isCommentMessage && reviewRating > 0;
                              const messageTargetKey = `${message.reviewFeedItemId}::${message.reviewCommentId ?? ''}`;
                              const isAnswerTargetMessage = answerTargetKey === messageTargetKey;
                              const isOfficialMessage = message.isOfficial === true;
                              const canSelectAsAnswerTarget = isAnswerTargetSelectable(message);

                              return (
                                  <div
                                      key={idx}
                                      ref={isAnswerTargetMessage ? replyTargetMessageRef : undefined}
                                      className={clsx(
                                          'self-start ml-1 p-1.5 rounded-lg max-w-[80%] text-sm leading-5 whitespace-pre-line wrap-break-word',
                                          isOfficialMessage ? 'bg-emerald-50' : 'bg-black/6',
                                          canSelectAsAnswerTarget
                                              ? isAnswerTargetMessage
                                                  ? 'cursor-pointer'
                                                  : 'cursor-pointer hover:ring-1 hover:ring-amber-200'
                                              : 'cursor-default',
                                          isAnswerTargetMessage && 'ring-2 ring-amber-400',
                                      )}
                                      data-reply-target={isAnswerTargetMessage ? 'true' : 'false'}
                                      onClick={
                                          canSelectAsAnswerTarget ? () => onAnswerTargetSelect(message) : undefined
                                      }
                                      onMouseEnter={() => handleMessageMouseEnter(message)}
                                      onMouseLeave={handleMessageMouseLeave}
                                  >
                                      {isAnswerTargetMessage && (
                                          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                                              Цель ответа
                                          </p>
                                      )}
                                      {shouldShowRating && (
                                          <div className="mb-1 flex items-center gap-1">
                                              <Rating value={reviewRating} />
                                              <span className="text-xs text-slate-600">{`${reviewRating}/5`}</span>
                                          </div>
                                      )}
                                      {preparedReviewText || '[Отзыв без текста]'}

                                      {reviewPhotos.length > 0 && (
                                          <div className="mt-2 flex items-center gap-1 overflow-x-auto">
                                              {reviewPhotos.map((photo, index) => (
                                                  <button
                                                      type="button"
                                                      key={`${message.reviewFeedItemId}-photo-${index}`}
                                                      aria-label={`Открыть фото ${index + 1}`}
                                                      className="h-[30px] w-[30px] shrink-0 overflow-hidden rounded border border-slate-200 transition-colors hover:border-slate-500"
                                                      onClick={(event) => {
                                                          event.stopPropagation();
                                                          onPhotoPreviewOpen(reviewPhotos, index);
                                                      }}
                                                  >
                                                      {/* eslint-disable-next-line @next/next/no-img-element */}
                                                      <img
                                                          src={photo.miniSize}
                                                          alt={`Фото ${index + 1}`}
                                                          className="h-full w-full object-cover"
                                                      />
                                                  </button>
                                              ))}
                                          </div>
                                      )}

                                      {(reviewCreatedDateLabel || isOfficialMessage) && (
                                          <div
                                              className={clsx(
                                                  'text-gray-500 text-xs mt-1 flex justify-end',
                                                  reviewCreatedDateLabel &&
                                                      isOfficialMessage &&
                                                      'gap-4 justify-between',
                                              )}
                                          >
                                              {reviewCreatedDateLabel && <span>{reviewCreatedDateLabel}</span>}
                                              {isOfficialMessage && <span>Официальный ответ</span>}
                                          </div>
                                      )}
                                  </div>
                              );
                          })(),
                )}
                {isChatHistoryDebugJsonEnabled && hoveredMessageDebugJson && (
                    <div className="rounded border border-amber-300 bg-amber-50 p-2 text-[11px] leading-4 text-amber-900">
                        <p className="mb-1 font-semibold">DEBUG JSON</p>
                        <pre className="max-h-28 overflow-auto whitespace-pre-wrap break-all">
                            {hoveredMessageDebugJson}
                        </pre>
                    </div>
                )}
                <div className="mt-auto bg-white pt-2">
                    <div className="flex flex-col gap-2">
                        {isManualEditOpened ? (
                            <LabeledTextarea
                                title="ИИ-ответ"
                                value={messageText}
                                setValue={setMessageText}
                                disabled={textAreaDisabled}
                                readOnly
                                placeholder={'Нажмите "ИИ-ответ" или введите свой текст сообщения'}
                            />
                        ) : (
                            <>
                                <Textarea
                                    placeholder={'Нажмите "ИИ-ответ" или введите свой текст сообщения'}
                                    className="min-h-[126px] border border-gray-300"
                                    value={messageText}
                                    setValue={setMessageText}
                                    disabled={textAreaDisabled}
                                    autoGrow
                                />
                                <AnswerTemplateVariableHint padded />
                            </>
                        )}
                        {aiErrorText && <p className="text-red-600 text-xs px-1">{aiErrorText}</p>}
                        {isManualEditOpened && (
                            <>
                                <div className="flex justify-center">
                                    <button
                                        type="button"
                                        aria-label="Скопировать текст в поле правки"
                                        className="h-8 w-8 rounded-full border border-slate-200 bg-white flex items-center justify-center transition-colors hover:bg-slate-100 disabled:cursor-default disabled:bg-slate-200"
                                        onClick={copyMessageToEdited}
                                        disabled={textAreaDisabled}
                                    >
                                        <Image src={arrow} alt={''} width={16} height={16} className="rotate-90" />
                                    </button>
                                </div>

                                <LabeledTextarea
                                    title="К отправке"
                                    value={editedMessageText}
                                    setValue={setEditedMessageText}
                                    disabled={textAreaDisabled}
                                    placeholder={'Введите правку (необязательно)'}
                                />
                                <AnswerTemplateVariableHint padded />
                            </>
                        )}
                    </div>
                </div>
            </div>
            <div className="shrink-0 border-t border-slate-200 bg-white pt-2">
                <DialogActionGrid
                    templatesAction={
                        <AnswerTemplatesControl
                            answerTemplates={answerTemplates}
                            isTemplatesLoading={isTemplatesLoading}
                            templatesErrorText={templatesErrorText}
                            isTemplatesMenuOpen={isTemplatesMenuOpen}
                            isTemplatesEditorShown={isTemplatesEditorShown}
                            editingTemplateId={editingTemplateId}
                            templateNameDraft={templateNameDraft}
                            templateTextDraft={templateTextDraft}
                            templateFormErrorText={templateFormErrorText}
                            isTemplateSaving={isTemplateSaving}
                            isTemplateDeleting={isTemplateDeleting}
                            canSaveTemplate={canSaveTemplate}
                            canDeleteTemplate={canDeleteTemplate}
                            onToggleTemplatesMenu={onToggleTemplatesMenu}
                            onCloseTemplatesMenu={onCloseTemplatesMenu}
                            onOpenTemplatesEditor={onOpenTemplatesEditor}
                            onCloseTemplatesEditor={onCloseTemplatesEditor}
                            onApplyTemplate={onApplyTemplate}
                            onSelectTemplateForEdit={onSelectTemplateForEdit}
                            onStartNewTemplateDraft={onStartNewTemplateDraft}
                            onTemplateNameDraftChange={onTemplateNameDraftChange}
                            onTemplateTextDraftChange={onTemplateTextDraftChange}
                            onSaveTemplate={onSaveTemplate}
                            onDeleteTemplate={onDeleteTemplate}
                            buttonClassName="h-full w-full"
                        />
                    }
                    aiAction={
                        <QuotaActionButton
                            reason={aiGenerateDisabledReason}
                            isQuotaExhausted={isAiGenerateQuotaExhausted}
                            tooltipClassName="h-full w-full"
                            priorityType={UIPriority.SECONDARY}
                            disabled={aiGenerateDisabled}
                            onClick={generateAiChatMessage}
                            className="h-full w-full"
                            uiSize="compact"
                        >
                            {aiGenerateLabel}
                        </QuotaActionButton>
                    }
                    autoRepliesAction={
                        <Button
                            type="button"
                            priorityType={UIPriority.SECONDARY}
                            onClick={onOpenSelectedAutoReplies}
                            className="h-full w-full"
                            uiSize="compact"
                        >
                            {selectedAutoRepliesLabels.action}
                        </Button>
                    }
                    editAction={
                        canShowManualEditButton ? (
                            <Button
                                priorityType={UIPriority.SECONDARY}
                                onClick={openManualEdit}
                                className="h-full w-full"
                                uiSize="compact"
                            >
                                Правка
                            </Button>
                        ) : null
                    }
                    primaryAction={
                        <DisabledReasonTooltip reason={submitDisabledReason} className="h-full w-full">
                            <Button
                                priorityType={UIPriority.PRIMARY}
                                uiSize="compact"
                                disabled={!canSubmitMessage}
                                onClick={submitSendChatMessage}
                                className="flex h-full w-full items-center justify-center whitespace-normal px-2 text-center leading-tight"
                            >
                                {submitButtonLabel}
                            </Button>
                        </DisabledReasonTooltip>
                    }
                />
            </div>
            {openedPhotos && (
                <Modal
                    isShown={true}
                    setIsShown={onPhotoPreviewClose}
                    hideHeader
                    className="h-[90vh] w-[90vw] max-w-[1000px]"
                >
                    <div className="relative h-full w-full bg-slate-900 p-4">
                        <button
                            type="button"
                            aria-label="Закрыть просмотр фото"
                            className="absolute right-4 top-4 z-10 h-8 w-8 rounded-full bg-white/90 text-sm text-slate-900 transition-colors hover:bg-white"
                            onClick={onPhotoPreviewClose}
                        >
                            x
                        </button>
                        <div className="flex h-full items-center justify-center gap-4">
                            <button
                                type="button"
                                aria-label="Предыдущее фото"
                                className="h-10 w-10 rounded-full bg-white/90 text-xl text-slate-900 transition-colors hover:bg-white disabled:cursor-default disabled:bg-white/40"
                                onClick={onPreviousPhoto}
                                disabled={openedPhotos.photos.length < 2}
                            >
                                {'<'}
                            </button>
                            <div className="relative flex h-full w-full items-center justify-center">
                                {isPhotoLoading && (
                                    <div className="absolute inset-0 flex items-center justify-center rounded bg-slate-800/70 text-sm text-slate-200">
                                        Загрузка фото...
                                    </div>
                                )}
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    key={`${openedPhotos.index}-${openedPhoto?.fullSize || ''}`}
                                    src={openedPhoto?.fullSize || ''}
                                    alt={`Фото ${openedPhotos.index + 1}`}
                                    className={`max-h-full max-w-full object-contain transition-opacity duration-150 ${
                                        isPhotoLoading ? 'opacity-0' : 'opacity-100'
                                    }`}
                                    onLoad={onOpenedPhotoResolved}
                                    onError={onOpenedPhotoResolved}
                                />
                            </div>
                            <button
                                type="button"
                                aria-label="Следующее фото"
                                className="h-10 w-10 rounded-full bg-white/90 text-xl text-slate-900 transition-colors hover:bg-white disabled:cursor-default disabled:bg-white/40"
                                onClick={onNextPhoto}
                                disabled={openedPhotos.photos.length < 2}
                            >
                                {'>'}
                            </button>
                        </div>
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded bg-black/70 px-3 py-1 text-sm text-white">
                            {openedPhotos.index + 1} / {openedPhotos.photos.length}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};
